import { Controller } from "@hotwired/stimulus"
import Honeybadger from "@honeybadger-io/js"

// Drag a customer row onto a Kundengruppe header in the grouped Kunden list (BAUTASK-114).
//
// ONE delegated controller on the list container (the planning_board_drop_controller approach):
// rows are plain `[data-customer-id]` elements with draggable="true", headers are plain
// `[data-customer-group-drop="<group id | none>"]` markers. Native HTML5 drag events, no
// SortableJS — the rows sit inside lazily loaded turbo-frames of many sections.
//
// A drop POSTs the one customer to the Sammelzuordnung endpoint with the list's current filter
// (queryValue) and renders the returned Turbo Stream, which re-renders the source and target
// sections. Dropping a row on the section it already sits in does nothing.
export default class extends Controller {
  static values = { url: String, query: String }

  static HIGHLIGHT = ["bg-green-50", "ring-2", "ring-inset", "ring-green-400"]

  connect() {
    this.dragged = null
    this.activeTarget = null
  }

  dragstart(event) {
    const row = event.target.closest?.("[data-customer-id]")
    if (!row) return

    this.dragged = { id: row.dataset.customerId, groupKey: row.dataset.customerGroupKey }
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", row.dataset.customerId)
  }

  dragover(event) {
    const target = this.targetFor(event)
    if (!this.dragged || !target) return this.highlight(null)

    // preventDefault on dragover is what allows the drop to fire.
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
    this.highlight(target)
  }

  dragleave(event) {
    // dragleave bubbles from every child the pointer crosses; only clear when leaving the header.
    if (this.activeTarget && !this.activeTarget.contains(event.relatedTarget)) this.highlight(null)
  }

  dragend() {
    this.reset()
  }

  async drop(event) {
    const target = this.targetFor(event)
    const dragged = this.dragged
    this.reset()
    if (!target || !dragged) return

    event.preventDefault()
    const groupKey = target.dataset.customerGroupDrop
    if (groupKey === dragged.groupKey) return

    await this.assign(dragged.id, groupKey)
  }

  async assign(customerId, groupKey) {
    const body = new URLSearchParams()
    body.append("customer_ids[]", customerId)
    body.append("customer_group_id", groupKey === "none" ? "" : groupKey)

    try {
      const response = await fetch(this.requestUrl, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token": this.csrfToken,
          Accept: "text/vnd.turbo-stream.html",
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body,
      })
      const contentType = response.headers.get("Content-Type") || ""
      if (response.ok && contentType.includes("text/vnd.turbo-stream.html")) {
        Turbo.renderStreamMessage(await response.text())
        return
      }
      this.fail(`customer group drop failed: ${response.status}`)
    } catch (error) {
      this.fail(error)
    }
  }

  // Anything but a stream means the page no longer matches the server: reload it, so the list
  // shows the real state (and the server's flash) instead of a silently failed move.
  fail(error) {
    Honeybadger.notify(error, { context: { component: "customer-group-drop" } })
    window.location.reload()
  }

  targetFor(event) {
    return event.target.closest?.("[data-customer-group-drop]") || null
  }

  highlight(target) {
    if (this.activeTarget === target) return
    this.activeTarget?.classList.remove(...this.constructor.HIGHLIGHT)
    target?.classList.add(...this.constructor.HIGHLIGHT)
    this.activeTarget = target
  }

  reset() {
    this.highlight(null)
    this.dragged = null
  }

  get requestUrl() {
    return this.queryValue ? `${this.urlValue}?${this.queryValue}` : this.urlValue
  }

  get csrfToken() {
    return document.head.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
  }
}
